import CustomLoadingButton from 'Components/CustomLoadingButton'
import FormTextField from 'Components/FormTextField'
import PTAFormField from 'Components/PTAFormField'
import StyledForm from 'Components/StyledForm'
import { WithLoader } from 'Components/WithLoader'
import type { FieldValidationErrors } from 'Services/ControlDataManagement/types'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import { isSuperuser } from 'Services/common/Features/User/userSlice'
import { SELECTED_SCHOOL_PARAMETER_KEY } from 'Services/SchoolManagement/Features/School/Hooks/useSchoolsWithParams'
import {
  useCreateSingleSchoolMutation,
  useUpdateSingleSchoolMutation
} from 'Services/SchoolManagement/Features/School/schoolApi'
import type { School } from 'Services/SchoolManagement/Features/School/types'
import { useGetSchoolCalendarsQuery } from 'Services/SchoolManagement/Features/SchoolCalendar/schoolCalendarApi'
import { useTypedSelector } from 'Store'
import type { ControlDataServiceError } from 'Util/ApiClient'
import { FORM_FIELD_DEFAULT_MIN_WIDTH } from 'Util/constants'
import { useValidationErrors } from 'Util/hooks/useValidationErrors'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material'
import { Box, Grid } from '@mui/system'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

const fieldErrorsDefaultState: FieldValidationErrors = {
  school_id: [],
  school_unit_id: [],
  name: [],
  calendar_id: []
}

interface SchoolFormProps {
  successBaseUrl: string
  school?: School
}

export default function SchoolForm({ school, successBaseUrl }: Readonly<SchoolFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!school
  const [searchParams] = useSearchParams()
  const selectedSchoolId = searchParams.get(SELECTED_SCHOOL_PARAMETER_KEY)
  const { user } = useTypedSelector(state => state.user)
  const { selectedAuthorityCode } = useAuthorityContext()

  const { fieldErrors, setValidationErrors, getFieldErrorHelperText, getErrorProps } =
    useValidationErrors(fieldErrorsDefaultState)

  const [createSingleSchool, createStatus] = useCreateSingleSchoolMutation()
  const [updateSingleSchool, updateStatus] = useUpdateSingleSchoolMutation()

  const mutationStatus = isEdit ? updateStatus : createStatus
  const { isLoading: isSubmitLoading } = mutationStatus

  const [schoolDetails, setSchoolDetails] = useState<Partial<School>>(
    (isEdit && school) || {
      school_id: selectedSchoolId ?? '',
      codespace_id: selectedAuthorityCode ?? user?.pta
    }
  )

  const {
    data: { resources: schoolCalendars } = { resources: [] },
    isFetching: isSchoolCalendarsFetching
  } = useGetSchoolCalendarsQuery({
    bypass_pagination: true,
    codespace_id: schoolDetails.codespace_id
  })

  const handleChange = (value: string | undefined, field: keyof School) => {
    if (value !== undefined) {
      setSchoolDetails(prevState => ({ ...prevState, [field]: value }) as School)
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const schoolData = {
      ...schoolDetails,
      // Backend expects null value for calendar_id when the school doesn't have a calendar
      calendar_id: schoolDetails.calendar_id === '' ? null : schoolDetails.calendar_id
    }

    const mutation = isEdit
      ? updateSingleSchool({ id: school.id, ...schoolData })
      : createSingleSchool(schoolData)

    mutation
      .unwrap()
      .then((schoolResponse: School) => {
        SnackbarUtils.success(t`School successfully saved.`)
        if (isEdit) {
          navigate(successBaseUrl)
        } else {
          navigate(
            appendQueryParamsToUrl(successBaseUrl, {
              [SELECTED_SCHOOL_PARAMETER_KEY]: schoolResponse.id
            })
          )
        }
      })
      .catch(({ data: errorResponse }: { data: ControlDataServiceError }) => {
        const { violations } = errorResponse
        setValidationErrors(violations)
        SnackbarUtils.error(t`There was an error saving the school.`)
      })
  }

  const schoolCalendarOptions = schoolCalendars.map(calendar => ({
    label: `${calendar.calendar_id} - ${calendar.name}`,
    value: calendar.calendar_id
  }))
  // Add empty option for school calendar
  schoolCalendarOptions.unshift({
    label: '-',
    value: ''
  })

  const handlePtaValueChange = (selectedPta: string | undefined) => {
    if (selectedPta !== schoolDetails.codespace_id) {
      handleChange('', 'calendar_id')
    }
    handleChange(selectedPta, 'codespace_id')
  }

  return (
    <WithLoader loading={isSchoolCalendarsFetching}>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading}>
            {!isEdit && isSuperuser(user) && (
              <Box>
                <PTAFormField value={schoolDetails.codespace_id} onChange={handlePtaValueChange} />
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box>
                <FormTextField
                  name="school_id"
                  required
                  label={t`School identifier`}
                  value={schoolDetails?.school_id ?? ''}
                  maxLength={40}
                  onChange={e => handleChange(e.target.value, 'school_id')}
                  {...getErrorProps('school_id')}
                />
              </Box>
              <Box>
                <FormTextField
                  name="school_unit_id"
                  disabled={isEdit}
                  required
                  label={t`School unit`}
                  value={schoolDetails?.school_unit_id ?? ''}
                  maxLength={40}
                  onChange={e => handleChange(e.target.value, 'school_unit_id')}
                  {...getErrorProps('school_unit_id')}
                />
              </Box>
            </Box>
            <Box>
              <FormTextField
                name="name"
                required
                label={t`Name`}
                value={schoolDetails?.name ?? ''}
                onChange={e => handleChange(e.target.value, 'name')}
                {...getErrorProps('name')}
              />
            </Box>
            <Box>
              <FormControl sx={{ width: FORM_FIELD_DEFAULT_MIN_WIDTH }} size="small">
                <InputLabel>{t`Calendar`}</InputLabel>
                <Select
                  name="calendar_id"
                  size="small"
                  label={t`Calendar`}
                  value={schoolDetails.calendar_id ?? ''}
                  onChange={e => {
                    handleChange(e.target.value as string, 'calendar_id')
                  }}
                  error={fieldErrors.calendar_id.length > 0}
                >
                  {schoolCalendarOptions.map(option => (
                    <MenuItem key={`${option.label}-${option.value}`} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error={fieldErrors.calendar_id.length > 0}>
                  {getFieldErrorHelperText('calendar_id')}
                </FormHelperText>
              </FormControl>
            </Box>
            <Box>
              <CustomLoadingButton
                type="submit"
                size="large"
                iconPosition="end"
                isLoading={isSubmitLoading}
              >{t`Save school`}</CustomLoadingButton>
            </Box>
          </StyledForm>
        </Grid>
      </Grid>
    </WithLoader>
  )
}
